import React from 'react';
import { View, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome';
import { pickPhotos, toFormFile, type PickedPhoto } from '../Common/photoPicker';
import { Text, Card, Divider } from 'react-native-paper';
import moment from 'moment';
import { useGetAmAssetByIdQuery } from '../redux/services/amassetApi';
import {
  useGetAmDocumentsQuery,
  useUploadAmDocumentMutation,
  useDeleteAmDocumentMutation
} from '../redux/services/amdocumentApi';
import { ShowToast } from '../Common/common';
import AmPhoto from '../Components/AmPhoto';
import { assetAge } from '../Common/assetAge';

const Row = ({ label, value }: { label: string; value?: string | number | null }) =>
  value === undefined || value === null || value === '' ? null : (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{String(value)}</Text>
    </View>
  );

const money = (v?: number | null) => (v != null ? `Rs ${Number(v).toLocaleString()}` : null);

const AmAssetDetail = ({ route }: any) => {
  const assetId: number = route?.params?.assetId;
  const { data: asset, isLoading } = useGetAmAssetByIdQuery(assetId, { skip: !assetId });
  const { data: docs = [] } = useGetAmDocumentsQuery(assetId, { skip: !assetId });
  const photos = docs.filter(d => d.ed_category === 'Photo');
  const [uploadDocument, { isLoading: isUploading }] = useUploadAmDocumentMutation();
  const [deleteDocument] = useDeleteAmDocumentMutation();

  const upload = async (a: PickedPhoto) => {
    const fd = new FormData();
    fd.append('file', toFormFile(a));
    fd.append('as_asset', String(assetId));
    fd.append('ed_reftype', 'Asset');
    fd.append('category', 'Photo');
    try {
      await uploadDocument(fd).unwrap();
      ShowToast('Photo added');
    } catch (e: any) {
      ShowToast(e?.data?.message || 'Could not upload photo', 'error');
    }
  };

  const addPhotos = async (source: 'camera' | 'library') => {
    try {
      for (const p of await pickPhotos(source)) await upload(p);
    } catch (e: any) {
      ShowToast(e?.message || 'Could not open the camera', 'error');
    }
  };
  const takePhoto = () => addPhotos('camera');
  const pickPhoto = () => addPhotos('library');

  const removePhoto = async (edId: number) => {
    try {
      await deleteDocument(edId).unwrap();
    } catch (e: any) {
      ShowToast(e?.data?.message || 'Could not remove photo', 'error');
    }
  };

  if (isLoading || !asset) {
    return <ActivityIndicator size="large" color="#2E7D32" style={{ flex: 1 }} />;
  }

  const age = assetAge(asset.as_purchasedate);

  return (
    <ScrollView style={styles.container}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.photos}>
        {photos.map(p => (
          <AmPhoto
            key={p.ed_id}
            edId={p.ed_id}
            size={140}
            // purchase-line photos belong to the purchase; only the asset's own can be removed here
            onRemove={p.ed_asset ? () => removePhoto(p.ed_id as number) : undefined}
          />
        ))}
        {isUploading ? (
          <ActivityIndicator color="#2E7D32" style={{ width: 70 }} />
        ) : (
          <View style={{ justifyContent: 'center', gap: 8 }}>
            <TouchableOpacity style={styles.photoBtn} onPress={takePhoto}>
              <Icon name="camera" size={18} color="#2E7D32" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.photoBtn} onPress={pickPhoto}>
              <Icon name="image" size={18} color="#2E7D32" />
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>
      <Card style={styles.card}>
        <Text style={styles.name}>{asset.as_name}</Text>
        <Text style={styles.code}>{asset.as_code}</Text>
        <Divider style={styles.divider} />
        <Row label="Status" value={asset.as_status} />
        <Row label="Product" value={asset.product_name ? [asset.product_name, asset.product_brand, asset.product_model].filter(Boolean).join(" · ") : null} />
        <Row label="Category" value={asset.ct_name} />
        <Row label="Department" value={asset.dp_name} />
        <Row label="Location" value={asset.loc_name || asset.as_location} />
        <Row label="Serial No." value={asset.as_serialno} />
        <Row label="Quantity" value={(asset.as_qty ?? 1) > 1 ? asset.as_qty : null} />
        <Divider style={styles.divider} />
        <Row label="Purchase date" value={asset.as_purchasedate ? moment(asset.as_purchasedate).format('DD-MMM-YYYY') : null} />
        <Row label="Age" value={age} />
        <Row label="Invoice No." value={asset.as_invoiceno} />
        <Row label="Purchase cost" value={money(asset.as_purchasecost)} />
        <Row label="Book value" value={money(asset.bookValue)} />
        <Row label="Depreciation" value={asset.as_depmethod} />
        <Row label="Useful life (yrs)" value={asset.as_usefullife} />
        <Row label="Description" value={asset.as_notes} />
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  photos: { padding: 10, gap: 8 },
  photoBtn: { width: 64, height: 64, borderRadius: 6, borderWidth: 1, borderStyle: 'dashed', borderColor: '#2E7D32', alignItems: 'center', justifyContent: 'center' },
  card: { margin: 10, padding: 14 },
  name: { fontSize: 18, fontWeight: 'bold' },
  code: { fontSize: 13, color: '#666', marginTop: 2 },
  divider: { marginVertical: 10 },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  label: { fontSize: 13, color: '#666' },
  value: { fontSize: 13, fontWeight: '600', flexShrink: 1, textAlign: 'right', marginLeft: 12 }
});

export default AmAssetDetail;
